import React, { useMemo, useRef, useState, useEffect } from "react";
import { Box, Typography, Divider, CircularProgress, Popper, ClickAwayListener } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import { gql, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { restaurantList } from "../../apollo/server";
import { useLocationContext } from "../../context/Location";
import HeaderSearchBar from "./HeaderSearchBar";
import AltNames from "../MenuName/AltNames";
import { CATERING_COLOR, CATERING_SOFT, queryMentionsCatering } from "../../utils/catering";
import { resizedImage } from "../../utils/resizedImage";

const RESTAURANTS = gql`
  ${restaurantList}
`;

// Case-insensitive "does haystack contain the query" — escaped so user input
// like "(" never breaks the match.
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// DoorDash-style search dropdown anchored under the header search bar.
// Reuses the nearByRestaurants data the list page already loads and filters it
// client-side into: query suggestions, matching restaurants, and matching
// dishes (by item NAME + category only — not descriptions, to avoid fuzzy hits).
// deferQuery: off the Restaurants screen the nearby-restaurants list isn't
// loaded yet, so it waits for the customer to actually reach for search.
export default function SearchDropdown({ value, onChange, placeholder, deferQuery = false }) {
  const navigate = useNavigate();
  const { location } = useLocationContext();
  // Open/close model: the panel is rendered in a Popper portaled to <body> so
  // no ancestor's overflow/stacking context can clip it (the fixed AppBar on
  // mobile was swallowing the absolutely-positioned panel). It opens on focus
  // and closes via ClickAwayListener — which handles touch reliably, so the
  // old blur-timer dance is no longer needed.
  const [focused, setFocused] = useState(false);
  const [armed, setArmed] = useState(!deferQuery);
  const [debounced, setDebounced] = useState(value || "");
  const wrapRef = useRef(null);

  // Debounce so we don't re-filter on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced((value || "").trim()), 150);
    return () => clearTimeout(t);
  }, [value]);

  const handleFocus = () => {
    setFocused(true);
    setArmed(true);
  };

  const { data, loading } = useQuery(RESTAURANTS, {
    variables: {
      longitude: location?.longitude || null,
      latitude: location?.latitude || null,
    },
    fetchPolicy: "cache-first",
    skip: !location || !armed,
  });

  const allRestaurants = useMemo(
    () => data?.nearByRestaurants?.restaurants ?? [],
    [data]
  );

  const results = useMemo(() => {
    const q = debounced.toLowerCase();
    if (!q) return { restaurants: [], dishes: [], suggestions: [] };
    const re = new RegExp(escapeRe(q), "i");

    // Rank restaurants: name match (2) > cuisine match (1).
    const restMatches = [];
    const dishMap = new Map(); // dish name → { name, restaurants:Set, slug, id }
    const cuisineHits = new Set(); // matched cuisine names → suggestion chips

    for (const r of allRestaurants) {
      let score = 0;
      if (re.test(r.name || "")) score = 2;
      else {
        // Cuisine tags live under .cuisines (and sometimes .tags) — match either.
        const cuisineList = [...(r.cuisines || []), ...(r.tags || [])];
        const matched = cuisineList.filter((c) => re.test(c || ""));
        if (matched.length) {
          score = 1;
          matched.forEach((c) => cuisineHits.add(c));
        }
      }
      if (score > 0) restMatches.push({ r, score });

      // Dishes: advanced-menu item NAME or category only (no descriptions).
      // The AI-translated names count as names too, so typing "బిర్యానీ" or
      // "比尔亚尼" finds the dish. Every stored translation is searched, even
      // ones the owner has not published to the menu.
      for (const m of r.advancedMenus || []) {
        for (const it of m.items || []) {
          if (
            re.test(it.name || "") ||
            re.test(it.categoryName || "") ||
            re.test(it.alternateName1 || "") ||
            re.test(it.alternateName2 || "")
          ) {
            const key = (it.name || "").toLowerCase();
            if (!key) continue;
            if (!dishMap.has(key)) {
              dishMap.set(key, {
                name: it.name,
                altName1: it.alternateName1 || null,
                altName2: it.alternateName2 || null,
                langDisplay: r.menuLanguageDisplay || "NONE",
                restaurants: [],
              });
            }
            const entry = dishMap.get(key);
            if (!entry.restaurants.find((x) => x._id === r._id)) {
              entry.restaurants.push({ _id: r._id, name: r.name, slug: r.slug, image: r.image, logo: r.logo });
            }
          }
        }
      }
    }

    restMatches.sort((a, b) => b.score - a.score || (a.r.distance ?? 1e9) - (b.r.distance ?? 1e9));

    const dishes = [...dishMap.values()].slice(0, 5);
    // Keyword suggestions: the raw query, matched cuisines, then top dish names.
    const suggestions = [
      debounced,
      ...[...cuisineHits].filter((c) => c.toLowerCase() !== q),
      ...dishes.map((d) => d.name).filter((n) => n.toLowerCase() !== q),
    ].slice(0, 3);

    return {
      restaurants: restMatches.slice(0, 6).map((x) => x.r),
      dishes,
      suggestions,
    };
  }, [debounced, allRestaurants]);

  const goToRestaurant = (r) => {
    setFocused(false);
    navigate(`/restaurant/${r.slug}`, { state: { id: r._id, name: r.name, image: r.image, slug: r.slug } });
  };
  const seeAll = () => {
    setFocused(false);
    navigate("/restaurant-list");
  };
  // Searching for catering offers the Catering page first.
  const showCatering = queryMentionsCatering(debounced);
  const goToCatering = () => {
    setFocused(false);
    navigate("/catering");
  };

  const fmtMi = (d) => (d == null ? null : `${Number(d).toFixed(1)} mi`);
  const fmtRating = (r) => {
    const n = r?.reviewData?.ratings || r?.rating;
    return n ? Number(n).toFixed(1) : null;
  };

  const hasQuery = !!debounced;
  const showPanel = hasQuery && focused;

  return (
    <ClickAwayListener onClickAway={() => setFocused(false)}>
      <Box ref={wrapRef} sx={{ position: "relative", flex: 1, minWidth: 0 }}>
        <HeaderSearchBar
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          onFocus={handleFocus}
          onKeyDown={(e) => { if (e.key === "Enter") seeAll(); }}
        />

        <Popper
          open={showPanel}
          anchorEl={wrapRef.current}
          placement="bottom-start"
          // Portal to <body> so the fixed AppBar's overflow/stacking context
          // can't clip the panel (the bug that hid it entirely on mobile).
          // Match the anchor width and sit above the AppBar (zIndex 1100).
          style={{ width: wrapRef.current?.offsetWidth, zIndex: 1300 }}
          modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        >
          <Box
            sx={{
              width: "100%",
              bgcolor: "#fff",
              borderRadius: "12px",
              boxShadow: "0 8px 28px rgba(0,0,0,0.16)",
              border: "1px solid #eee",
              maxHeight: "70vh",
              overflowY: "auto",
              py: 1,
            }}
          >
          {loading && allRestaurants.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <>
              {showCatering && (
                <Box
                  onClick={goToCatering}
                  sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1, cursor: "pointer", "&:hover": { bgcolor: "#f7f7f7" } }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      flexShrink: 0,
                      bgcolor: CATERING_SOFT,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <RoomServiceRoundedIcon sx={{ color: CATERING_COLOR, fontSize: 22 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: CATERING_COLOR }}>Catering</Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: "#767676" }} noWrap>
                      See restaurants that cater near you
                    </Typography>
                  </Box>
                </Box>
              )}
              {showCatering && results.suggestions.length > 0 && <Divider sx={{ my: 0.5 }} />}

              {/* Query suggestions */}
              {results.suggestions.map((s, i) => (
                <Box
                  key={`sug-${i}`}
                  onClick={seeAll}
                  sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1, cursor: "pointer", "&:hover": { bgcolor: "#f7f7f7" } }}
                >
                  <SearchIcon sx={{ color: "#767676", fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 600, color: "#191919" }}>{s}</Typography>
                </Box>
              ))}

              {/* Restaurants */}
              {results.restaurants.length > 0 && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <Typography sx={{ px: 2, py: 0.5, fontSize: "0.72rem", fontWeight: 700, color: "#767676", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Restaurants
                  </Typography>
                  {results.restaurants.map((r) => {
                    const rating = fmtRating(r);
                    const mi = fmtMi(r.distance);
                    const cuisines = (r.cuisines || []).slice(0, 2).join(", ");
                    const hasDeal = (r.activePromotionCount || 0) > 0 || (r.offers || []).length > 0;
                    return (
                      <Box
                        key={r._id}
                        onClick={() => goToRestaurant(r)}
                        sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1, cursor: "pointer", "&:hover": { bgcolor: "#f7f7f7" } }}
                      >
                        <Box
                          component="img"
                          src={resizedImage(r.logo || r.image, 128)}
                          alt=""
                          sx={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0, bgcolor: "#eee" }}
                          onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontWeight: 600, color: "#191919" }} noWrap>{r.name}</Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#767676", fontSize: "0.8rem", flexWrap: "wrap" }}>
                            {rating && (
                              <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                                <StarIcon sx={{ fontSize: 13, color: "#191919", mr: 0.25 }} />
                                <Typography component="span" sx={{ fontSize: "0.8rem", color: "#191919", fontWeight: 600 }}>{rating}</Typography>
                              </Box>
                            )}
                            {mi && <Typography component="span" sx={{ fontSize: "0.8rem" }}>· {mi}</Typography>}
                            {cuisines && <Typography component="span" sx={{ fontSize: "0.8rem" }} noWrap>· {cuisines}</Typography>}
                          </Box>
                        </Box>
                        {hasDeal && (
                          <Typography sx={{ flexShrink: 0, fontSize: "0.72rem", fontWeight: 700, color: "#CC0000" }}>Deal</Typography>
                        )}
                      </Box>
                    );
                  })}
                </>
              )}

              {/* Dishes */}
              {results.dishes.length > 0 && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <Typography sx={{ px: 2, py: 0.5, fontSize: "0.72rem", fontWeight: 700, color: "#767676", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Dishes
                  </Typography>
                  {results.dishes.map((d) => (
                    <Box
                      key={d.name}
                      onClick={() => d.restaurants[0] && goToRestaurant(d.restaurants[0])}
                      sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1, cursor: "pointer", "&:hover": { bgcolor: "#f7f7f7" } }}
                    >
                      <RestaurantMenuIcon sx={{ color: "#767676", fontSize: 20 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, color: "#191919" }} noWrap>
                          {d.name}
                          <AltNames alt1={d.altName1} alt2={d.altName2} displayMode={d.langDisplay} />
                        </Typography>
                        <Typography sx={{ fontSize: "0.8rem", color: "#767676" }} noWrap>
                          {d.restaurants.length === 1 ? d.restaurants[0].name : `${d.restaurants.length} restaurants`}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </>
              )}

              {/* Empty */}
              {results.restaurants.length === 0 && results.dishes.length === 0 && !showCatering && (
                <Typography sx={{ px: 2, py: 2, color: "#767676", fontSize: "0.9rem" }}>
                  No results for "{debounced}". Press Enter to see all restaurants.
                </Typography>
              )}
            </>
          )}
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
